import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'ic-bottom-bar-dismissed-until';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Slim static (no motion) bar on blog pages inviting non-members to join.
 * Dismissible — returns after 7 days (persisted in localStorage).
 * Hidden entirely for members.
 */
const InnerCircleBottomBar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (user?.is_member) { setVisible(false); return; }
    try {
      const until = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
      setVisible(!until || Date.now() > until);
    } catch (_e) {
      setVisible(true);
    }
  }, [user?.is_member]);

  if (!visible || user?.is_member) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + SEVEN_DAYS_MS));
    } catch (_e) { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Inner Circle invitation"
      data-testid="ic-bottom-bar"
      className="fixed inset-x-0 bottom-0 z-40 border-t"
      style={{
        background: 'linear-gradient(90deg, #1a0f05 0%, #0f0802 100%)',
        borderColor: 'rgba(212,168,67,0.35)',
      }}
    >
      <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex items-center gap-3 text-[12px] sm:text-sm text-primary/85 min-w-0">
          <span className="font-heading tracking-[0.25em] uppercase text-primary">
            Inner Circle
          </span>
          <span className="opacity-70 hidden sm:inline">·</span>
          <span className="opacity-90 truncate">₹108 a month</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => navigate('/inner-circle')}
            className="text-[11px] sm:text-xs font-heading uppercase tracking-[0.25em] rounded-full border border-primary/55 text-primary px-3 py-1 hover:bg-primary/10"
            data-testid="ic-bottom-bar-join"
          >
            Join
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="p-1 text-primary/60 hover:text-primary"
            aria-label="Dismiss"
            data-testid="ic-bottom-bar-dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InnerCircleBottomBar;
